/**
 * 檢視範圍（View Scope）— 去品牌化 M1 的全站唯一切換軸。
 *
 * 語意：這是「讀取視角」的過濾器，只影響列表 / 統計要顯示哪個品牌的資料，
 * 完全不改 JWT、不打 switch-brand、不 reload、也絕不作為寫入的預設品牌。
 * 寫入一律走顯式 targetBrand（M4 TargetBrandField），不繼承本檢視範圍。
 *
 * 值域：
 *   - 'all'           = 聚合檢視（彙總全部可見品牌；D2 admin 預設）
 *   - <brand code>    = 單一品牌檢視
 *
 * 優先序（讀初始值）：URL ?scope= > localStorage 'viewScope' > 預設 'all'。
 * 跨分頁同步靠 storage event。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getBrandOptions,
  tokenManager,
  type Brand,
} from './tokenManager';

/** 聚合模式哨兵值。 */
export const ALL_SCOPE = 'all';

const STORAGE_KEY = 'viewScope';
const BRAND_CODE_PATTERN = /^[a-z][a-z0-9_]{1,15}$/;

export type ViewScope = typeof ALL_SCOPE | Brand;

interface ViewScopeContextValue {
  /** 目前檢視範圍（'all' 或單一品牌 code）。 */
  scope: ViewScope;
  /** 設定檢視範圍（寫入 localStorage，觸發跨分頁同步）。 */
  setScope: (next: ViewScope) => void;
  /** 是否為聚合檢視。 */
  isAllScope: boolean;
  /**
   * 目前檢視範圍對應的品牌 code；聚合模式回 null。
   * authFetch 用它決定要不要送 X-Brand（聚合=省略）。
   */
  brandForRequest: Brand | null;
}

const ViewScopeContext = createContext<ViewScopeContextValue | null>(null);

/** 合法值 = catalog 成員 ∪ 'all'（其餘一律 clamp 回 'all'）。 */
function isValidScope(value: unknown): value is ViewScope {
  if (value === ALL_SCOPE) return true;
  if (typeof value !== 'string' || !BRAND_CODE_PATTERN.test(value)) return false;
  const allowed = tokenManager.getAllowedBrands();
  return allowed.includes(value);
}

function readInitialScope(): ViewScope {
  // URL ?scope= 優先（深連結可重現）。
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('scope');
    if (fromUrl && isValidScope(fromUrl)) return fromUrl;
  } catch {
    // ignore malformed URL
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isValidScope(stored)) return stored;
  } catch {
    // ignore storage errors
  }

  return ALL_SCOPE;
}

/**
 * 目前檢視範圍以外必須被 clamp：
 * - 非全品牌管理員（brandScope 非 'all'）只能檢視自己被授權的品牌，
 *   若目前值不在授權集合內就退回第一個授權品牌（沒有就 'all'，由後端再擋）。
 */
function clampScopeToAllowed(scope: ViewScope): ViewScope {
  if (tokenManager.isAllBrandAdmin()) return scope;
  const allowed = tokenManager.getAllowedBrands();
  if (scope !== ALL_SCOPE && allowed.includes(scope)) return scope;
  // 品牌管理員不給聚合檢視，鎖回自己的品牌。
  return allowed[0] ?? ALL_SCOPE;
}

interface ProviderProps {
  children: ReactNode;
}

export function ViewScopeProvider({ children }: ProviderProps) {
  const [scope, setScopeState] = useState<ViewScope>(() =>
    clampScopeToAllowed(readInitialScope()),
  );

  const setScope = useCallback((next: ViewScope) => {
    const clamped = clampScopeToAllowed(next);
    // Keep authFetch's module snapshot in step with a deep-link scope change
    // before consumers may issue their first request after the React update.
    currentViewScope = clamped;
    setScopeState(clamped);
    try {
      localStorage.setItem(STORAGE_KEY, clamped);
    } catch {
      // ignore storage errors
    }
  }, []);

  // 跨分頁同步：另一個分頁改了 viewScope → 本頁跟著更新。
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      const next = event.newValue;
      if (next && isValidScope(next)) {
        setScopeState(clampScopeToAllowed(next));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<ViewScopeContextValue>(() => {
    const isAllScope = scope === ALL_SCOPE;
    return {
      scope,
      setScope,
      isAllScope,
      brandForRequest: isAllScope ? null : scope,
    };
  }, [scope, setScope]);

  return (
    <ViewScopeContext.Provider value={value}>
      {children}
    </ViewScopeContext.Provider>
  );
}

/**
 * 消費檢視範圍。
 *
 * authFetch 在 React 樹外呼叫，改讀 module-level snapshot（見 getViewScopeBrandForRequest），
 * 元件內一律用本 hook。
 */
export function useViewScope(): ViewScopeContextValue {
  const ctx = useContext(ViewScopeContext);
  if (!ctx) {
    throw new Error('useViewScope 必須在 <ViewScopeProvider> 內使用');
  }
  return ctx;
}

// ─────────────────────────────────────────────────────────────────────────
// authFetch 用的 module-level 橋接
//
// authFetch 不是 React component，拿不到 context。Provider 把目前值鏡射到
// 這個 module 變數，authFetch 只讀不寫。聚合模式回 null → 呼叫端省略 X-Brand。
// ─────────────────────────────────────────────────────────────────────────

let currentViewScope: ViewScope = ALL_SCOPE;

/** 供 authFetch 讀取：單品牌回 brand code、聚合回 null。 */
export function getViewScopeBrandForRequest(): Brand | null {
  return currentViewScope === ALL_SCOPE ? null : currentViewScope;
}

/** 初始化 module snapshot（在 App 載入時呼叫一次，讓 authFetch 一開始就有正確值）。 */
export function primeViewScopeSnapshot(): void {
  currentViewScope = clampScopeToAllowed(readInitialScope());
}

/**
 * 讓 module snapshot 跟著 context 走的 side-effect hook。
 * 在 Provider 底下掛一次即可。
 */
export function useSyncViewScopeSnapshot(): void {
  const { scope } = useViewScope();
  useEffect(() => {
    currentViewScope = scope;
  }, [scope]);
}

/** 品牌 code 對應顯示名；聚合回「全部品牌（彙總）」。 */
export function getViewScopeLabel(scope: ViewScope): string {
  if (scope === ALL_SCOPE) return '全部品牌（彙總）';
  return getBrandOptions().find((o) => o.value === scope)?.label ?? scope;
}
